import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import ReportCatalog from "@/pages/admin/reports/components/ReportCatalog";
import BatchExportPanel from "@/pages/admin/reports/components/BatchExportPanel";
import ExportTaskHistory from "@/pages/admin/reports/components/ExportTaskHistory";
import { useReportCenter } from "@/pages/admin/reports/hooks/useReportCenter";
import { LINK_REPORTS, RUNNABLE_REPORTS, type RunnableReport } from "@/lib/reportCatalog";
import { HEALTH_WINDOWS } from "@/lib/webhookHealth";
import { formatNumber } from "@/lib/format";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { tasks, loading, error, reload, runReport, runBatchReports, healthWindow, setHealthWindow, context } =
    useReportCenter();
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  // 每份报表的预计行数：由报表上下文按当前数据 / 窗口估算，供批量导出的范围确认弹窗使用。
  const estimates = useMemo(
    () =>
      Object.fromEntries(
        RUNNABLE_REPORTS.map((report) => [report.key, report.estimate ? report.estimate(context) : 0]),
      ),
    [context],
  );

  const notify = (text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  };

  const handleRun = async (report: RunnableReport) => {
    setRunningKey(report.key);
    try {
      const count = await runReport(report);
      notify(t("reports.runnableDone", { name: report.name, rows: formatNumber(count) }));
    } catch (err) {
      notify(err instanceof Error ? err.message : t("reports.runnableFailed"), "error");
    } finally {
      setRunningKey(null);
    }
  };

  const handleRunBatch = async (reports: RunnableReport[]) => {
    if (reports.length === 0) {
      notify(t("reports.batchEmpty"), "error");
      return;
    }
    setBatchRunning(true);
    try {
      const result = await runBatchReports(reports);
      notify(t("reports.batchDone", { count: result.count, rows: formatNumber(result.total) }));
    } catch {
      notify(t("reports.batchFailed"), "error");
    } finally {
      setBatchRunning(false);
    }
  };

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const monthCount = tasks.filter((task) => new Date(task.created_at).getTime() >= monthStart).length;
  const totalRows = tasks.reduce((sum, task) => sum + (task.row_count || 0), 0);

  const stats = [
    { label: t("reports.statTotal"), value: tasks.length, icon: "ri-file-chart-line", tone: "bg-primary-100 text-primary-700" },
    { label: t("reports.statMonth"), value: monthCount, icon: "ri-calendar-check-line", tone: "bg-accent-100 text-accent-700" },
    { label: t("reports.statRunnable"), value: RUNNABLE_REPORTS.length, icon: "ri-flashlight-line", tone: "bg-secondary-100 text-secondary-900" },
    { label: t("reports.statRows"), value: totalRows, icon: "ri-list-check-2", tone: "bg-primary-100 text-primary-700" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("reports.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("reports.breadcrumbAdmin")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("reports.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-file-chart-line" />
                  </span>
                  {t("reports.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("reports.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("reports.subtitle")}
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                {t("reports.backToAdmin")}
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("reports.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-md ${item.tone}`}>
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <p className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : formatNumber(item.value)}
                    </p>
                    <p className="mt-1 text-sm text-foreground-600">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-heart-pulse-line" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{t("reports.healthWindowTitle")}</p>
                    <p className="text-[11px] text-foreground-500">{t("reports.healthWindowHint")}</p>
                  </div>
                </div>
                <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
                  {HEALTH_WINDOWS.map((window) => (
                    <button
                      key={window}
                      type="button"
                      onClick={() => setHealthWindow(window)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                        healthWindow === window
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {t(`approvals.healthWindow${window}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <BatchExportPanel
                  reports={RUNNABLE_REPORTS}
                  loading={loading}
                  running={batchRunning}
                  onRun={handleRunBatch}
                  estimates={estimates}
                />
              </div>

              <div className="mt-8">
                <ReportCatalog
                  reports={RUNNABLE_REPORTS}
                  linkReports={LINK_REPORTS}
                  loading={loading}
                  runningKey={runningKey}
                  onRun={handleRun}
                  healthWindow={healthWindow}
                  onHealthWindowChange={setHealthWindow}
                />
              </div>

              <div className="mt-8">
                <ExportTaskHistory tasks={tasks} loading={loading} />
              </div>
            </>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}